"use client";

import { usePathname } from "next/navigation";
import { useGlobalComponentData } from "@/lib/storyblok/hooks/useGlobalComponentData";
import type { LocationPage } from "@/generated/sb";
import React from "react";
import {
  storyblokEditable,
  StoryblokServerComponent,
  StoryblokStory,
} from "@storyblok/react/rsc";
import { Main } from "@repo/ui-parkmobile/Main";
import { BreadcrumbsSection } from "./sections/BreadcrumbsSection";
import { sectionResolver } from "@/lib/storyblok/adaptors";
import { groupSections } from "@/lib/groupSections";
import { LocationPageSchema } from "./schema";
import { useGlobalDataContext } from "./GlobalDataContext";
import { deleteAutoSlug } from "@/utils/autoSlug";

export interface LocationPageProps {
  blok: LocationPage;
}

export function LocationPage({ blok }: LocationPageProps) {
  const pathname = usePathname();
  const { currentSlug } = useGlobalDataContext();
  const globalHeader = useGlobalComponentData(blok.header?.[0] as string);
  const globalFooter = useGlobalComponentData(blok.footer?.[0] as string);

  const { content } = blok;

  if (!content) return <div>No content</div>;

  const groupedSections = groupSections(content);

  // TODO: Move this paddings to Storyblok sections settings
  // Remove top padding based on breadcrumbs position
  // Skip this logic for the main parking/locations page
  const normalizedSlug = deleteAutoSlug(currentSlug ?? "");
  const isMainLocationsPage = normalizedSlug === "parking/locations";

  if (!isMainLocationsPage) {
    const firstSection = groupedSections[0]?.sections[0];

    if (firstSection?.component === "heroWide") {
      let nextSection = groupedSections[0]?.sections[1];
      if (!nextSection && groupedSections[1]) {
        nextSection = groupedSections[1].sections[0];
      }

      if (nextSection) {
        nextSection.topPadding = "none";
        nextSection.paddingTop = "none";
      }
    } else if (firstSection) {
      firstSection.topPadding = "none";
      firstSection.paddingTop = "none";
    }
  }

  return (
    <div className={`uipm:flex uipm:flex-col uipm:min-h-full`}>
      <LocationPageSchema blok={blok} pathname={pathname} />

      {globalHeader && (
        <StoryblokStory
          story={globalHeader}
          bridgeOptions={{
            resolveLinks: "url",
          }}
        />
      )}
      <Main {...storyblokEditable(blok)}>
        {groupedSections.map((group, groupIndex) => {
          return group.sections.map((section, sectionIndex) => {
            const isFirstSection = groupIndex === 0 && sectionIndex === 0;
            const sectionComponent = section.component;

            const showBreadcrumbsBefore =
              isFirstSection && sectionComponent === "locationCard";

            const showBreadcrumbsAfter =
              isFirstSection && sectionComponent === "heroWide";

            return (
              <React.Fragment key={section._uid}>
                {showBreadcrumbsBefore && (
                  <BreadcrumbsSection
                    backgroundColor="none"
                    topPadding="none"
                    bottomPadding="none"
                  />
                )}
                <StoryblokServerComponent
                  blok={section}
                  isHeroSection={isFirstSection}
                  pageType="location"
                  {...storyblokEditable(section)}
                  {...sectionResolver(section)}
                />
                {showBreadcrumbsAfter && (
                  <BreadcrumbsSection
                    backgroundColor="none"
                    topPadding="none"
                    bottomPadding="none"
                  />
                )}
              </React.Fragment>
            );
          });
        })}
      </Main>
      {globalFooter && (
        <StoryblokStory
          story={globalFooter}
          bridgeOptions={{
            resolveLinks: "url",
          }}
        />
      )}
    </div>
  );
}
